// F37.5-ANDROID-SHARE-INTENT — routing layer for shares received from the
// Android system share menu. Creates an outfit row, fires the
// analyse-outfit-social orchestrator, and emits a window CustomEvent so
// Like2FitApp can swap to the results screen with the new outfit_id.
//
// Mirrors the in-app patterns in src/components/nav/UrlPasteSheet.tsx
// (URL submission) and SocialPlatformSheet → screenshot upload — the
// downstream contract on the orchestrator is identical regardless of
// whether the trigger is the in-app sheet or a native share intent.

import { supabase } from "@/integrations/supabase/client";
import { trackEvent } from "@/lib/analytics";
import { invokeGenerator } from "@/lib/invokeGenerator";

import type { SharePayload } from "@/lib/shareIntent";
import { compressImage } from "@/lib/imageCompression";
import { Filesystem, Directory } from "@capacitor/filesystem";

const LOG = "[share-handler]";

export type SocialPlatform = "instagram" | "tiktok" | "pinterest" | "facebook";

const PLATFORM_PATTERNS: Record<SocialPlatform, RegExp> = {
  instagram: /https?:\/\/(www\.)?instagram\.com\/(p|reel|tv)\/[A-Za-z0-9_-]+/i,
  tiktok: /https?:\/\/((www\.|vm\.|vt\.)?tiktok\.com\/[^\s]+)/i,
  pinterest: /https?:\/\/((www\.|[a-z]{2}\.)?pinterest\.[a-z.]+\/pin\/[0-9]+|pin\.it\/[A-Za-z0-9]+)/i,
  // SOCIAL-SHARE-FACEBOOK-1 — full Facebook support (posts, reels, watch,
  // fb.watch short links). Story URLs match here too; the orchestrator
  // rejects them with a user-facing "Stories can't be shared" surface.
  facebook: /https?:\/\/((www\.|m\.|web\.|mbasic\.)?facebook\.com\/[^\s]+|fb\.watch\/[A-Za-z0-9_-]+)/i,
};

const SHARED_OUTFIT_EVENT = "like2fit:shared-outfit";

/**
 * `kind` selects which pending screen the dispatcher mounts:
 *   - 'social-url' | 'image' → SharePendingResults (image preview / hero shell)
 *   - 'generic-url'          → UrlPendingResults  (hostname echo, URL watcher)
 *   - 'text'                 → TextPendingResults (text echo, text watcher)
 *
 * `source` / `platform` are retained for back-compat with existing
 * listeners; new code should switch on `kind`.
 */
export type SharedOutfitKind = "social-url" | "generic-url" | "text" | "image";

export interface SharedOutfitDetail {
  outfitId: string;
  kind: SharedOutfitKind;
  source: "url" | "image";
  platform: SocialPlatform | null;
  // F37.5-SHARE-FLOW-COMPLETION-FIX (Phase 1): base64 data URL of the shared
  // image, for instant preview in SharePendingResults before any DB read.
  // Only populated for image shares.
  previewDataUrl?: string;
  // F.5c.js — context for per-type pending screens.
  hostname?: string; // generic-url only
  text?: string;     // text only
  userId?: string | null; // pass-through; auth read once per share
  // F.5h-v2 — storage path of the image written to the outfits row at INSERT
  // time. Present for image shares (always) and for URL shares when the iOS
  // share-extension supplied an og:image preview that uploaded successfully.
  // When present, the dispatcher mounts Results directly via viewOutfit and
  // skips the SharePendingResults/UrlPendingResults intermediate screen.
  imagePath?: string | null;
  // P1a.5-iii — full source URL for URL shares (social + generic). Used by the
  // instant-mount IIFE to populate synthetic row's source_url field so the
  // URL hero variant can echo the link before Apify completes. Absent for
  // image and text kinds.
  sourceUrl?: string | null;
}

/** Extract the first http(s) URL from arbitrary share-sheet text. */
export function extractUrl(text: string): string | null {
  const match = text.match(/https?:\/\/\S+/);
  return match ? match[0].trim() : null;
}

/**
 * SOCIAL-SHARE-FACEBOOK-1 — classify Facebook URLs into a coarse shape for
 * telemetry (facebook_share_attempted). Kept local so the funnel query can
 * distinguish Reels from Watch from Stories without parsing the raw URL.
 */
function classifyFacebookUrlShape(
  url: string,
): "post" | "reel" | "watch" | "fb_watch" | "story" | "other" {
  if (/^https?:\/\/fb\.watch\//i.test(url)) return "fb_watch";
  if (/facebook\.com\/stories\//i.test(url)) return "story";
  if (/facebook\.com\/reel\//i.test(url)) return "reel";
  if (/facebook\.com\/watch\//i.test(url)) return "watch";
  if (/facebook\.com\/[^/]+\/posts\//i.test(url)) return "post";
  return "other";
}

export function detectPlatform(url: string): SocialPlatform | null {
  for (const [platform, pattern] of Object.entries(PLATFORM_PATTERNS) as [
    SocialPlatform,
    RegExp,
  ][]) {
    if (pattern.test(url)) {
      if (platform === "facebook") {
        // Fire-and-forget demand signal — surfaces in the analytics_events
        // table with event_type = 'facebook_share_attempted'.
        try {
          trackEvent("facebook_share_attempted", {
            url_shape: classifyFacebookUrlShape(url),
          });
        } catch {
          /* analytics never blocks share routing */
        }
      }
      return platform;
    }
  }
  return null;
}


interface AuthSnapshot {
  user_id: string | null;
  is_guest: boolean;
  guest_session_id: string | null;
}

async function readAuthSnapshot(): Promise<AuthSnapshot> {
  const { data } = await supabase.auth.getSession();
  const userId = data.session?.user?.id ?? null;
  const guestSessionId =
    typeof window !== "undefined" ? localStorage.getItem("guest_session_id") : null;
  return {
    user_id: userId,
    is_guest: !userId,
    guest_session_id: !userId ? guestSessionId : null,
  };
}

async function readProfileDefaults(userId: string | null): Promise<{
  gender: "female" | "male" | "non_binary";
  model_setup: Record<string, unknown> | null;
  user_budget: number | null;
  budget_top: number | null;
  budget_bottom: number | null;
  budget_shoes: number | null;
}> {
  if (!userId) {
    return {
      gender: "female",
      model_setup: null,
      user_budget: null,
      budget_top: null,
      budget_bottom: null,
      budget_shoes: null,
    };
  }
  const { data } = await supabase
    .from("profiles")
    .select(
      "model_gender, user_budget, budget_top, budget_bottom, budget_shoes",
    )
    .eq("id", userId)
    .maybeSingle();
  const p = (data ?? {}) as Record<string, unknown>;
  const g = (p.model_gender as string) ?? "female";
  const gender: "female" | "male" | "non_binary" =
    g === "male" || g === "non_binary" ? (g as "male" | "non_binary") : "female";
  return {
    gender,
    model_setup: null,
    user_budget: (p.user_budget as number | null) ?? null,
    budget_top: (p.budget_top as number | null) ?? null,
    budget_bottom: (p.budget_bottom as number | null) ?? null,
    budget_shoes: (p.budget_shoes as number | null) ?? null,
  };
}

type ShareSourceType = "social" | "url" | "text" | "photo";

interface PreviewImageInput {
  /** Path relative to Capacitor Directory.Cache, e.g. "shares/share_<UUID>_preview.jpg". */
  filename: string;
  mimeType: string;
}

interface UploadedImage {
  storagePath: string;
  signedUrl: string;
}

/**
 * Phase 3b — upload the iOS share-extension's og:image preview to the
 * `outfits` bucket so it can be set as the outfit row's image_url at INSERT
 * time. Returns null on any failure; callers MUST fall back to the no-image
 * path (Apify still patches the better frame in via realtime).
 *
 * Reads the JPEG bytes from Capacitor Cache, uploads as-is (extension already
 * encoded at 0.85 quality, ≤2MB), then best-effort deletes the cache file.
 */
async function uploadPreviewImage(
  preview: PreviewImageInput,
  outfitId: string,
  auth: AuthSnapshot,
): Promise<UploadedImage | null> {
  let base64: string;
  try {
    const result = await Filesystem.readFile({
      path: preview.filename,
      directory: Directory.Cache,
    });
    base64 = typeof result.data === "string" ? result.data : "";
    if (!base64) throw new Error("empty_file_data");
  } catch (err) {
    console.warn(LOG, "preview readFile failed", err);
    return null;
  }
  const mime = preview.mimeType || "image/jpeg";
  const ext = extFromMime(mime);
  const folderId = auth.user_id ?? `guests/${auth.guest_session_id ?? "anonymous"}`;
  const storagePath = `${folderId}/${outfitId}.${ext}`;
  const blob = base64ToBlob(base64, mime);
  const file = new File([blob], `preview_${outfitId}.${ext}`, { type: mime });
  const { error: upErr } = await supabase.storage
    .from("outfits")
    .upload(storagePath, file, { contentType: mime, upsert: false });
  if (upErr) {
    console.warn(LOG, "preview upload failed", upErr);
    void Filesystem.deleteFile({ path: preview.filename, directory: Directory.Cache })
      .catch(() => {});
    return null;
  }
  const { data: signed, error: signErr } = await supabase.storage
    .from("outfits")
    .createSignedUrl(storagePath, 3600);
  void Filesystem.deleteFile({ path: preview.filename, directory: Directory.Cache })
    .catch((err) => console.warn(LOG, "preview deleteFile failed", err));
  if (signErr || !signed?.signedUrl) {
    console.warn(LOG, "preview sign url failed", signErr);
    return null;
  }
  return { storagePath, signedUrl: signed.signedUrl };
}

async function createOutfitRow(
  auth: AuthSnapshot,
  sourceType: ShareSourceType,
  imageFields?: { storagePath: string } | null,
): Promise<string | null> {
  const outfitId = crypto.randomUUID();
  const row: Record<string, unknown> = {
    id: outfitId,
    user_id: auth.user_id,
    is_guest: auth.is_guest,
    guest_session_id: auth.guest_session_id,
    lookbook_status: "pending",
    // P1a.5-iv: branch input_method to match the 1a.5-iii synthetic row.
    //   image preview present  → "share_intent" (image hero path; img wins)
    //   text share              → "text"        (text hero variant)
    //   social-url / generic-url no image → "url" (url hero variant)
    // Audit (1a.5-iv): no RLS/edge-fn/analytics consumer filters on
    // "share_intent"; the only reader is setCurrentInputMethod which
    // drives the mount gate + hero variant. Persisted value now matches
    // the synthetic row used at instant-mount time.
    input_method: imageFields
      ? "share_intent"
      : sourceType === "text"
        ? "text"
        : "url",
    source_type: sourceType,
  };
  if (imageFields) {
    // Phase 3b — set image fields at INSERT time. Guest RLS does NOT permit
    // UPDATE on outfits for the anon role (memory #15), so we cannot patch
    // image_url after the fact for guest shares. The `signed://` sentinel
    // mirrors handleSharedImage/in-app handleAnalyse and is resolved by
    // viewOutfit's signImage helper. Apify realtime later overwrites
    // image_url with a higher-quality analysis frame (service-role write).
    row.image_path = imageFields.storagePath;
    row.image_url = `signed://${imageFields.storagePath}`;
    row.image_paths = [imageFields.storagePath];
  }
  const { error } = await supabase.from("outfits").insert(row as never);
  if (error) {
    console.error(LOG, "outfit insert failed", error);
    return null;
  }
  return outfitId;
}

/**
 * Resolve gender for text-share orchestration. Mirrors
 * TextDescriptionSheet.tsx:101-128 — profile.size_gender → profile.gender
 * for authed users; localStorage('guest_gender') for guests. Server
 * defaults to "women" when undefined.
 */
async function resolveTextGender(
  userId: string | null,
): Promise<"men" | "women" | undefined> {
  if (userId) {
    try {
      const { data: profile } = await supabase
        .from("profiles")
        .select("size_gender, gender")
        .eq("id", userId)
        .maybeSingle();
      const raw =
        (profile as { size_gender?: string | null; gender?: string | null } | null)
          ?.size_gender ??
        (profile as { gender?: string | null } | null)?.gender ??
        null;
      if (raw === "woman" || raw === "women") return "women";
      if (raw === "man" || raw === "men") return "men";
    } catch (err) {
      console.warn(LOG, "profile gender lookup failed", err);
    }
    return undefined;
  }
  if (typeof window !== "undefined") {
    const guestGender = window.localStorage.getItem("guest_gender");
    if (guestGender === "women" || guestGender === "men") return guestGender;
  }
  return undefined;
}

function emitSharedOutfit(detail: SharedOutfitDetail) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(SHARED_OUTFIT_EVENT, { detail }));
}

export function onSharedOutfit(
  handler: (detail: SharedOutfitDetail) => void,
): () => void {
  if (typeof window === "undefined") return () => {};
  const listener = (e: Event) => {
    const ce = e as CustomEvent<SharedOutfitDetail>;
    if (ce.detail) handler(ce.detail);
  };
  window.addEventListener(SHARED_OUTFIT_EVENT, listener);
  return () => window.removeEventListener(SHARED_OUTFIT_EVENT, listener);
}

function safeHostname(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return "the web";
  }
}

export async function handleSharedUrl(
  rawText: string,
  authOverride?: AuthSnapshot,
  preview?: PreviewImageInput | null,
): Promise<void> {
  const url = extractUrl(rawText);
  const auth = authOverride ?? (await readAuthSnapshot());

  // Phase 3b — upload og:image preview (iOS share-extension) BEFORE row insert
  // so image fields land on the same statement (guest RLS forbids UPDATE).
  // Preview only meaningful when the share contains a URL.
  let uploaded: UploadedImage | null = null;
  if (url && preview?.filename) {
    uploaded = await uploadPreviewImage(preview, crypto.randomUUID(), auth);
    // Note: outfitId used for storage path is independent of the row id —
    // it just needs to be unique. We could fold them together but keeping
    // them separate avoids reserving a UUID before insert succeeds.
  }


  // ---- Branch A: extractable URL ----
  if (url) {
    const platform = detectPlatform(url);

    // Social URL → analyse-outfit-social (unchanged behavior)
    if (platform) {
      const outfitId = await createOutfitRow(auth, "social", uploaded);
      if (!outfitId) return;

      trackEvent("share_intent_received", { source: "url", platform });
      emitSharedOutfit({
        outfitId,
        kind: "social-url",
        source: "url",
        platform,
        userId: auth.user_id,
        imagePath: uploaded?.storagePath ?? null,
        sourceUrl: url,
      });

      const defaults = await readProfileDefaults(auth.user_id);
      void invokeGenerator("analyse-outfit-social", {
        source: "share-intent-social",
        body: {
          outfit_id: outfitId,
          source_platform: platform,
          source_url: url,
          user_id: auth.user_id,
          is_guest: auth.is_guest,
          guest_session_id: auth.guest_session_id,
          gender: defaults.gender,
          model_setup: defaults.model_setup,
          user_budget: defaults.user_budget,
          budget_top: defaults.budget_top,
          budget_bottom: defaults.budget_bottom,
          budget_shoes: defaults.budget_shoes,
        },
      })
        .then(({ error, quotaExceeded }) => {
          if (quotaExceeded) return;
          if (error) {
            console.error(LOG, "analyse-outfit-social invoke failed", error);
            trackEvent("share_intent_invoke_failed", {
              source: "url",
              platform,
              reason: error.message ?? "unknown",
            });
          }
        });
      return;
    }


    // Generic (non-social) URL → analyse-outfit-url
    // Mirrors src/components/nav/UrlPasteSheet.tsx:197 body shape.
    const outfitId = await createOutfitRow(auth, "url", uploaded);
    if (!outfitId) return;

    const hostname = safeHostname(url);
    trackEvent("share_intent_received", { source: "url-generic" });
    emitSharedOutfit({
      outfitId,
      kind: "generic-url",
      source: "url",
      platform: null,
      hostname,
      userId: auth.user_id,
      imagePath: uploaded?.storagePath ?? null,
      sourceUrl: url,
    });

    const defaults = await readProfileDefaults(auth.user_id);
    void invokeGenerator("analyse-outfit-url", {
      source: "share-intent-url",
      body: {
        outfit_id: outfitId,
        url,
        user_id: auth.user_id,
        guest_session_id: auth.guest_session_id,
        user_budget: defaults.user_budget,
        budget_top: defaults.budget_top,
        budget_bottom: defaults.budget_bottom,
        budget_shoes: defaults.budget_shoes,
      },
    })
      .then(({ error, quotaExceeded }) => {
        if (quotaExceeded) return;
        if (error) {
          console.error(LOG, "analyse-outfit-url invoke failed", error);
          trackEvent("share_intent_invoke_failed", {
            source: "url-generic",
            reason: error.message ?? "unknown",
          });
        }
      });

    return;
  }

  // ---- Branch B: no URL → plain text description ----
  // Mirrors src/components/nav/TextDescriptionSheet.tsx:132 body shape.
  const trimmed = rawText.trim();
  if (!trimmed) return;

  const outfitId = await createOutfitRow(auth, "text");
  if (!outfitId) return;

  trackEvent("share_intent_received", { source: "text" });
  emitSharedOutfit({
    outfitId,
    kind: "text",
    source: "url",
    platform: null,
    text: trimmed,
    userId: auth.user_id,
  });

  const gender = await resolveTextGender(auth.user_id);
  void invokeGenerator("analyse-outfit-text", {
    source: "share-intent-text",
    body: {
      outfit_id: outfitId,
      text_description: trimmed,
      occasion: null,
      season: null,
      gender,
      user_id: auth.user_id,
      is_guest: auth.is_guest,
      guest_session_id: auth.guest_session_id,
    },
  })
    .then(({ error, quotaExceeded }) => {
      if (quotaExceeded) return;
      if (error) {
        console.error(LOG, "analyse-outfit-text invoke failed", error);
        trackEvent("share_intent_invoke_failed", {
          source: "text",
          reason: error.message ?? "unknown",
        });
      }
    });

}


function extFromMime(mimeType: string): string {
  const m = mimeType.toLowerCase();
  if (m.includes("png")) return "png";
  if (m.includes("webp")) return "webp";
  if (m.includes("heic")) return "heic";
  return "jpg";
}

function base64ToBlob(base64: string, mimeType: string): Blob {
  const binary = atob(base64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mimeType });
}

/**
 * F.5d — image shares now follow the in-app upload pattern: read bytes
 * from Capacitor cache, upload binary to the `outfits` bucket, then
 * invoke analyse-outfit directly (bypassing analyse-outfit-social, which
 * is now social-URL only).
 */
export async function handleSharedImage(
  filename: string,
  mimeType: string,
  detectedPlatform: SocialPlatform | "facebook" | null = null,
  authOverride?: AuthSnapshot,
): Promise<void> {
  const auth = authOverride ?? (await readAuthSnapshot());

  // Social-app share → "social"; everything else → "photo".
  const imageSourceType: ShareSourceType =
    detectedPlatform === "instagram" ||
    detectedPlatform === "tiktok" ||
    detectedPlatform === "pinterest"
      ? "social"
      : "photo";

  const orchestratorPlatform: SocialPlatform | null =
    detectedPlatform === "instagram" ||
    detectedPlatform === "tiktok" ||
    detectedPlatform === "pinterest"
      ? detectedPlatform
      : null;

  // --- Read file from cache ------------------------------------------------
  let base64: string;
  try {
    const result = await Filesystem.readFile({
      path: filename,
      directory: Directory.Cache,
    });
    base64 = typeof result.data === "string" ? result.data : "";
    if (!base64) throw new Error("empty_file_data");
  } catch (err) {
    console.error(LOG, "Filesystem.readFile failed", err);
    trackEvent("share_intent_invoke_failed", {
      source: "image",
      reason: `read_failed:${(err as Error)?.message ?? "unknown"}`,
    });
    return;
  }

  // --- Compress (mirror in-app handleAnalyse) -----------------------------
  // compressImage scales longest edge to 1200px and re-encodes as JPEG@0.85.
  // Always JPEG output → upload mime + storage ext are fixed to image/jpeg.
  const outfitId = crypto.randomUUID();
  const folderId = auth.user_id ?? `guests/${auth.guest_session_id ?? "anonymous"}`;
  const sourceExt = extFromMime(mimeType);
  const sourceBlob = base64ToBlob(base64, mimeType);
  const sourceFile = new File(
    [sourceBlob],
    `share_${outfitId}.${sourceExt}`,
    { type: mimeType },
  );
  const compressedFile = await compressImage(sourceFile);
  const uploadMime = compressedFile.type || "image/jpeg";
  const uploadExt = extFromMime(uploadMime);
  const storagePath = `${folderId}/${outfitId}.${uploadExt}`;

  const { error: upErr } = await supabase.storage
    .from("outfits")
    .upload(storagePath, compressedFile, { contentType: uploadMime, upsert: false });
  if (upErr) {
    console.error(LOG, "storage upload failed", upErr);
    trackEvent("share_intent_invoke_failed", {
      source: "image",
      reason: `upload_failed:${upErr.message}`,
    });
    return;
  }

  // --- Sign URL ------------------------------------------------------------
  const { data: signed, error: signErr } = await supabase.storage
    .from("outfits")
    .createSignedUrl(storagePath, 3600);
  if (signErr || !signed?.signedUrl) {
    console.error(LOG, "sign url failed", signErr);
    trackEvent("share_intent_invoke_failed", {
      source: "image",
      reason: "sign_failed",
    });
    return;
  }
  const signedUrl = signed.signedUrl;

  // --- INSERT outfit row WITH image fields (single statement, no UPDATE) ---
  // RLS guest INSERT policy allows this; the previous separate UPDATE was
  // silently rejected for anon and left image_path NULL → blank results.
  // The `signed://` sentinel in image_url matches in-app handleAnalyse
  // (src/routes/index.tsx) and is resolved by viewOutfit's signImage helper.
  const { error: insErr } = await supabase
    .from("outfits")
    .insert({
      id: outfitId,
      user_id: auth.user_id,
      is_guest: auth.is_guest,
      guest_session_id: auth.guest_session_id,
      lookbook_status: "pending",
      input_method: "share_intent",
      source_type: imageSourceType,
      image_path: storagePath,
      image_url: `signed://${storagePath}`,
      image_paths: [storagePath],
    } as never);
  if (insErr) {
    console.error(LOG, "outfit insert failed", insErr);
    trackEvent("share_intent_invoke_failed", {
      source: "image",
      reason: `insert_failed:${insErr.message}`,
    });
    return;
  }

  // --- Best-effort delete of the cached source file ------------------------
  // Native orphan sweep is the safety net; don't block on failure.
  void Filesystem.deleteFile({ path: filename, directory: Directory.Cache })
    .catch((err) => console.warn(LOG, "deleteFile failed", err));

  trackEvent("share_intent_received", {
    source: "image",
    mime: mimeType,
    detected_platform: detectedPlatform ?? "unknown",
  });
  emitSharedOutfit({
    outfitId,
    kind: "image",
    source: "image",
    platform: orchestratorPlatform,
    previewDataUrl: `data:${mimeType};base64,${base64}`,
    userId: auth.user_id,
    imagePath: storagePath,
  });

  const defaults = await readProfileDefaults(auth.user_id);
  void supabase.functions
    .invoke("analyse-outfit", {
      body: {
        outfit_id: outfitId,
        source_type: imageSourceType === "social" ? "social" : "photo",
        source_platform: orchestratorPlatform,
        image_path: storagePath,
        image_url: signedUrl,
        user_id: auth.user_id,
        is_guest: auth.is_guest,
        guest_session_id: auth.guest_session_id,
        size_profile: null,
        model_setup: defaults.model_setup,
        gender: defaults.gender,
        user_budget: defaults.user_budget,
        budget_top: defaults.budget_top,
        budget_bottom: defaults.budget_bottom,
        budget_shoes: defaults.budget_shoes,
      },
    })
    .then(({ error }) => {
      if (error) {
        console.error(LOG, "analyse-outfit invoke failed", error);
        trackEvent("share_intent_invoke_failed", {
          source: "image",
          reason: error.message ?? "unknown",
        });
      }
    });
}

/**
 * Drain one pending share payload and route it. Safe to call repeatedly.
 * F.5c.js — reads auth ONCE here and passes it down, eliminating the
 * previous 3 round-trips (one per branch).
 */
export async function handleSharePayload(payload: SharePayload): Promise<void> {
  if (payload.type === "none") return;
  const auth = await readAuthSnapshot();
  if (payload.type === "text") {
    const preview = payload.previewImage
      ? { filename: payload.previewImage, mimeType: payload.previewMimeType ?? "image/jpeg" }
      : null;
    await handleSharedUrl(payload.text, auth, preview);
  } else if (payload.type === "image") {
    await handleSharedImage(
      payload.filename,
      payload.mimeType,
      payload.sourcePlatform ?? null,
      auth,
    );
  }
}

